import { defineStore, storeToRefs } from 'pinia';
import { ref } from 'vue';
import { TimePeriodInfo } from '@/common/components/molecules/timeController/types';
import {
  getInitPeriodInfo,
  getInitPeriodInfoByServerTime,
} from '@/common/components/molecules/timeController';
import {
  formatTimeRangeToTz,
  formatTimeRangeToUtc,
} from '@/common/components/molecules/timeController/utils';
import { getTimePeriodByRange } from '@/common/utils/core/time/stringify';
import { webStorageController } from '@/common/utils/browser';
import dayjs from 'dayjs';
import { standardTimeToUtcZeroTime } from '@/common/utils/commonUtils';
import { useDashboardViewStore } from './dashboard-view';
import { useMyInfoStore } from '@/config/stores/my-info';

export const useGlobalTimePeriodStore = defineStore('globalTimePeriod', () => {
  const { myInfo: userInfo } = storeToRefs(useMyInfoStore());
  const { monitoringDashboard } = storeToRefs(useDashboardViewStore());

  const globalTimePeriod = ref<TimePeriodInfo>(getInitPeriodInfo());
  const globalTimePeriodRecord = ref<Record<string, Omit<TimePeriodInfo, 'toTime' | 'fromTime'>>>(
    {},
  );
  const LOCAL_STORAGE_KEY = 'global-time-period';
  let isInit = false;

  const updateGlobalTimePeriodRecord = (timePeriodData: TimePeriodInfo) => {
    const dashboardId = monitoringDashboard.value.dashboardId;
    if (dashboardId === -1 || userInfo.value.userId === -1) {
      return;
    }

    const recordKey = `${userInfo.value.userId}-${dashboardId}`;
    globalTimePeriodRecord.value[recordKey] = {
      fromTimeUtc: timePeriodData.fromTimeUtc,
      toTimeUtc: timePeriodData.toTimeUtc,
      isPaused: timePeriodData.isPaused,
      timePeriod: timePeriodData.timePeriod,
    };
    webStorageController.setItem({
      type: 'local',
      key: LOCAL_STORAGE_KEY,
      value: JSON.stringify(globalTimePeriodRecord.value),
    });
  };

  const initGlobalTimePeriodRecord = () => {
    const initialGlobalTimePeriod = webStorageController.getItem({
      type: 'local',
      key: LOCAL_STORAGE_KEY,
    });
    if (initialGlobalTimePeriod) {
      globalTimePeriodRecord.value = JSON.parse(initialGlobalTimePeriod);
    }
  };

  const setGlobalTimePeriodByRecord = async (queryInfo?: {
    timeInfo?: string;
    alertId?: string;
    fromTime?: string;
    toTime?: string;
  }) => {
    // 복원 시점이 UI 마운트 순서에 의존하지 않도록 항상 최신 record를 먼저 읽는다.
    initGlobalTimePeriodRecord();

    const dashboardId = monitoringDashboard.value.dashboardId;
    if (dashboardId !== -1 && userInfo.value.userId !== -1) {
      const recordKey = `${userInfo.value.userId}-${dashboardId}`;
      const localStorageTimePeriod = globalTimePeriodRecord.value[recordKey];

      if (queryInfo?.timeInfo) {
        if (localStorageTimePeriod) {
          globalTimePeriod.value = {
            ...localStorageTimePeriod,
            ...formatTimeRangeToTz({
              fromTimeUtc: standardTimeToUtcZeroTime(
                dayjs(queryInfo.timeInfo).subtract(5, 'minute').format('YYYY-MM-DD HH:mm:ss'),
              ),
              toTimeUtc: standardTimeToUtcZeroTime(
                dayjs(queryInfo.timeInfo).add(5, 'minute').format('YYYY-MM-DD HH:mm:ss'),
              ),
            }),
            timePeriod: queryInfo.alertId ? 'p10m' : localStorageTimePeriod.timePeriod,
            isPaused: queryInfo.alertId ? true : localStorageTimePeriod.isPaused,
          };
        } else {
          globalTimePeriod.value = {
            ...(await getInitPeriodInfoByServerTime()),
            ...formatTimeRangeToTz({
              fromTimeUtc: standardTimeToUtcZeroTime(
                dayjs(queryInfo.timeInfo).subtract(5, 'minute').format('YYYY-MM-DD HH:mm:ss'),
              ),
              toTimeUtc: standardTimeToUtcZeroTime(
                dayjs(queryInfo.timeInfo).add(5, 'minute').format('YYYY-MM-DD HH:mm:ss'),
              ),
            }),
            timePeriod: 'p10m',
            isPaused: !!queryInfo.alertId,
          };
        }
        isInit = true;
      } else if (
        !Number.isNaN(Number(queryInfo?.fromTime)) &&
        !Number.isNaN(Number(queryInfo?.toTime))
      ) {
        const fromTime = dayjs(Number(queryInfo?.fromTime)).tz().format('YYYY-MM-DD HH:mm:ss');
        const toTime = dayjs(Number(queryInfo?.toTime)).tz().format('YYYY-MM-DD HH:mm:ss');
        const timePeriod = getTimePeriodByRange(fromTime, toTime).split(' ')[0];
        const { fromTimeUtc, toTimeUtc } = formatTimeRangeToUtc({ fromTime, toTime });

        globalTimePeriod.value = {
          fromTimeUtc,
          toTimeUtc,
          ...formatTimeRangeToTz({ fromTimeUtc, toTimeUtc }),
          timePeriod,
          isPaused: true,
        };
        isInit = true;
      } else {
        if (localStorageTimePeriod) {
          if (localStorageTimePeriod.isPaused) {
            globalTimePeriod.value = {
              ...localStorageTimePeriod,
              ...formatTimeRangeToTz({
                fromTimeUtc: localStorageTimePeriod.fromTimeUtc,
                toTimeUtc: localStorageTimePeriod.toTimeUtc,
              }),
            };
          } else {
            globalTimePeriod.value = {
              ...(await getInitPeriodInfoByServerTime({
                timePeriod: localStorageTimePeriod.timePeriod,
              })),
            };
          }
        } else {
          globalTimePeriod.value = {
            ...(await getInitPeriodInfoByServerTime({
              timePeriod: monitoringDashboard.value.dashboardOption.defaultPeriodRange || 'p10m',
            })),
          };
        }

        isInit = true;
      }
    }
  };

  const updateGlobalTimePeriod = (timePeriodData: TimePeriodInfo) => {
    globalTimePeriod.value = timePeriodData;
    if (isInit) {
      updateGlobalTimePeriodRecord(timePeriodData);
    }
  };

  const resetGlobalTimePeriodInit = () => {
    isInit = false;
  };

  const isPausedBySavingDashboard = ref(false);
  const pauseGlobalTimePeriod = () => {
    isPausedBySavingDashboard.value = true;
  };

  const initPausedBySavingDashboard = () => {
    isPausedBySavingDashboard.value = false;
  };

  return {
    globalTimePeriod,
    updateGlobalTimePeriod,

    initGlobalTimePeriodRecord,
    setGlobalTimePeriodByRecord,
    resetGlobalTimePeriodInit,

    isPausedBySavingDashboard,
    pauseGlobalTimePeriod,
    initPausedBySavingDashboard,
  };
});
